import { createContext, ReactNode, useReducer, useEffect } from 'react';
import { produce } from 'immer';
import { generateOtp, verifyOtpforPromoter, getProfileData } from '../shared/api/profile';
import { AuthContextType, AuthState, IHandleOtpSubmit } from '../utils/types/Promoter';
import * as localForage from 'localforage';

export const AuthContext = createContext<AuthContextType>({
	authState: {
		isLogined: false,
		token: '',
		otptoken: '',
		userProfile: null
	},
	authActions: {
		logout: async () => {},
		generateOtp: async () => {},
		promoterLogin: async () => {}
	}
});

const AUTH_LOGOUT = 'AUTH_LOGOUT';
const PROMOTER_OTP_VERIFICATION = 'PROMOTER_OTP_VERIFICATION';
const PROMOTER_AUTH_LOGIN = 'PROMOTER_AUTH_LOGIN';
const INITIALIZE_AUTH = 'INITIALIZE_AUTH';
const SET_USER_PROFILE = 'SET_USER_PROFILE';

const initialState: AuthState = {
	isLogined: false,
	token: '',
	otptoken: '',
	userProfile: null
};

const reducer = produce((state: AuthState, action) => {
	switch (action.type) {
		case PROMOTER_OTP_VERIFICATION:
			state.otptoken = action.payload.token;
			break;
		case PROMOTER_AUTH_LOGIN:
			state.isLogined = true;
			state.token = action.payload.token;
			break;
		case INITIALIZE_AUTH:
			state.isLogined = true;
			state.token = action.payload.token;
			break;
		case SET_USER_PROFILE:
			state.userProfile = action.payload.profile;
			break;
		case AUTH_LOGOUT:
			return initialState;
		default:
	}
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [authState, dispatch] = useReducer(reducer, initialState);

	useEffect(() => {
		const initializeAuth = async () => {
			try {
				const token = await localForage.getItem('token');
				if (token) {
					dispatch({ type: INITIALIZE_AUTH, payload: { token } });

					const { data } = await getProfileData(token as string);
					if (data.status && data.profile) {
						dispatch({ type: SET_USER_PROFILE, payload: { profile: data.profile } });
					}
				}
			} catch (err) {
				console.error('Error initializing auth:', err);
			}
		};
		initializeAuth();
	}, []);

	const action = {
		logout: async () => {
			try {
				await localForage.removeItem('token');
				await localForage.removeItem('promoterId');
				dispatch({ type: AUTH_LOGOUT });
			} catch (err) {
				console.error('Error during logout:', err);
			}
		},
		generateOtp: async (phoneNumber: string) => {
			try {
				const { data } = await generateOtp(phoneNumber);
				dispatch({ type: PROMOTER_OTP_VERIFICATION, payload: data });
			} catch (err: any) {
				throw err;
			}
		},
		promoterLogin: async (args: IHandleOtpSubmit) => {
			try {
				const { data } = await verifyOtpforPromoter(args);
				await localForage.setItem('token', data.token);
				// fetch user details to show on panel
				const profileResponse = await getProfileData(data.token);
				if (profileResponse.data.status && profileResponse.data.profile) {
					await localForage.setItem('promoterId', profileResponse.data.profile.omsPromoterReferenceId);
					dispatch({ type: SET_USER_PROFILE, payload: { profile: profileResponse.data.profile } });
				}
				dispatch({ type: PROMOTER_AUTH_LOGIN, payload: data });
			} catch (err: unknown) {
				throw err;
			}
		}
	};

	return <AuthContext.Provider value={{ authState: authState, authActions: action }}>{children}</AuthContext.Provider>;
};
